import { BasePack, OptimizedPack, PackOptionInformation } from "@/shared/interfaces/optimizedEncoding/PackSelection.interface";
import store from "@/shared/store";

type PackType = "basic" | "classic" | "premium";
interface PackConfigOptions {
    hideBillboard?: boolean;
}

// The price actually used for a pack: the discounted total when an automatic
// discount applies, otherwise the list price. Mirrors the order-total logic.
function effectivePrice(viz: any): number {
    if (!viz) return 0;
    return viz.automaticDiscount != null ? Number(viz.totalPrice) : Number(viz.price);
}

function durationInWeeks(duration: any): number {
    if (!duration) return 0;
    const unit = (duration.unit ?? "").toUpperCase();
    if (unit === "WEEK") return duration.count;
    if (unit === "DAY") return duration.count / 7;
    if (unit === "MONTH") return (duration.count * 30.42) / 7;
    return duration.count;
}

// The "+€X" extension add-on for a pack is the gap between that pack's alternative-tariff
// price and its base price — exactly what the order total already adds. The API's scalar
// extensionOption.price is only the BASIC gap, so it understates classic/premium. Derive
// the gap per pack here so the label matches the total. Falls back to the scalar when the
// per-pack alternative price or the base price is unavailable.
function perPackExtension(
    extensionOption: any,
    baseViz: any,
    packType: PackType,
): { price: number; pricePerWeek: number } {
    const extViz = extensionOption?.visualizationOptions?.[packType] ?? null;
    if (!extViz || !baseViz) {
        return {
            price: Number(extensionOption?.price ?? 0),
            pricePerWeek: Number(extensionOption?.pricePerWeek ?? 0),
        };
    }

    const price = Number((effectivePrice(extViz) - effectivePrice(baseViz)).toFixed(2));
    const weeks = durationInWeeks(extensionOption.duration);
    const pricePerWeek = weeks > 0 ? Number((price / weeks).toFixed(2)) : 0;

    return { price: price, pricePerWeek: pricePerWeek };
}

const basePacks: BasePack[] = [
    {
        type: "premium",
        title: "premium",
        subtitle: "premium_pack_subtitle",
        description: "pack_premium_description",
        imageSize: 100,
        mostPopular: false,
        defaultImage: require("@/assets/images/artworks/pack-pricing/newPremium.png"),
        options: ["premium_pack_images", "premium_pack_image_size", "premium_pack_video", "detailed_statistics", "premium_pack_priority", "premium_pack_similar_properties"]
    },
    {
        type: "classic",
        title: "classic",
        subtitle: "classic_pack_subtitle",
        description: "pack_classic_description",
        imageSize: 20,
        mostPopular: true,
        defaultImage: require("@/assets/images/artworks/pack-pricing/newClassic.png"),
        options: ["classic_pack_images", "classic_pack_image_size", "detailed_statistics", "classic_pack_priority"]
    },
    {
        type: "basic",
        title: "basic",
        subtitle: "basic_pack_subtitle",
        description: "pack_basic_description",
        imageSize: 10,
        mostPopular: false,
        defaultImage: require("@/assets/images/artworks/pack-pricing/newBasic.png"),
        options: ["basic_pack_images", "basic_pack_image_size", "basic_pack_priority"]
    }
];

export const defaultPackConfig: OptimizedPack[] = basePacks.map(pack => ({
    title: pack.title,
    subtitle: pack.subtitle,
    packDescription: pack.description,
    packType: pack.type,
    packPrice: "0",
    packPricePerWeek: "0",
    packPriceTotal: "0",
    cardOptions: pack.options,
    mostPopular: pack.mostPopular,
    imageSize: pack.imageSize,
    defaultImage: pack.defaultImage,
    additionalOption: {
        label: "additional_options",
        optionInformations: []
    },
    /* discountOption: pack.type === "premium"
        ? {
            label: "discount",
            optionInformations: [
                { type: "discount", label: "20% on virtual tour", pathInStore: `optimizedEncoding.packs.discount.${pack.type}`, isCheckbox: false },
                { type: "discount", label: "20% on 10 professional", pathInStore: `optimizedEncoding.packs.discount.${pack.type}`, isCheckbox: false }
            ]
        }
        : undefined */
    automaticDiscount: undefined,
}));

/**
 * Initialize packs based on stored values and transaction type
 */
export function configurePacksOnInit(
    storedPacks: any,
    transactionType: string,
    options: PackConfigOptions = {},
): OptimizedPack[] {
    const extensionDetails = store.getters["FormModule/getValueTransaction"]("transaction.pricing.extensionOptionDetails");
    const hideBillboard = options.hideBillboard === true;

    return defaultPackConfig.map(pack => {
        const storedPack = storedPacks?.[pack.packType];

        const optionInformations: PackOptionInformation[] = [];

        const billboardPrice = store.getters["FormModule/getValueTransaction"](`transaction.pricing.billboard.${pack.packType}`);
        // TODO: Change conditions whenever video feature is implemented
        const isFreeInPack = pack.packType === "premium" && transactionType === "FOR_SALE";
        const hasBillboardPrice = billboardPrice != null || storedPack?.billboardPrice != null;

        if (!hideBillboard && (isFreeInPack || hasBillboardPrice)) {
            optionInformations.push({
                type: "billboard",
                label: "v_shape_billboard",
                price: isFreeInPack ? "0" : (billboardPrice?.toString() ?? storedPack?.billboardPrice?.toString() ?? "0"),
                includedInPack: isFreeInPack,
                pathInStore: `optimizedEncoding.packs.billboard.${pack.packType}`,
                isCheckbox: true
            });
        }

        // Add extension dynamically if available. The extension is per-pack: each pack's
        // gap to the alternative tariff differs, so derive it from this pack's prices
        // rather than showing the API's basic-only scalar everywhere.
        if (extensionDetails) {
            const ext = perPackExtension(extensionDetails, storedPack, pack.packType as PackType);
            optionInformations.push({
                type: "extension",
                // Store the raw i18n key (not a pre-resolved string) so OptionCheckbox
                // translates it at render time and follows UI locale changes. IO-223
                label: "offer_duration_text_new",
                price: ext.price.toString(),
                pricePerWeek: ext.pricePerWeek.toString(),
                pathInStore: `optimizedEncoding.packs.extension.${pack.packType}`,
                isCheckbox: true
            });
        }

        return {
            ...pack,
            packPrice: storedPack?.price ?? pack.packPrice,
            packPricePerWeek: storedPack?.pricePerWeek ?? pack.packPricePerWeek,
            packPriceTotal: storedPack?.totalPrice ?? pack.packPriceTotal,
            automaticDiscount: storedPack?.automaticDiscount ?? undefined,
            additionalOption: {
                label: "additional_options",
                optionInformations: optionInformations
            }
        };
    });
}

/**
 * Update packs when duration or extension option changes
 */
export function configurePacksOnDurationChanged(
    selectedPricing: any,
    transactionType: string,
    options: PackConfigOptions = {},
): OptimizedPack[] {
    const visualizationOptions = selectedPricing?.visualizationOptions ?? {};
    const extensionOption = selectedPricing?.extensionOption ?? null;
    const billboardOption = selectedPricing?.billboard ?? null;
    const hideBillboard = options.hideBillboard === true;

    const currentConfiguredPacks: OptimizedPack[] =
        store.getters["FormModule/getValueTransaction"]("transaction.configuredPacks")
        || defaultPackConfig;

    return currentConfiguredPacks.map(pack => {
        const packType = pack.packType as PackType;
        const vis = visualizationOptions[packType] ?? null;

        const optionInformations: PackOptionInformation[] = [];

        /* ---------------- BILLBOARD ---------------- */
        if (!hideBillboard && billboardOption) {
            const billboardPrice = billboardOption[`${packType}Price`] ?? 0;

            optionInformations.push({
                type: "billboard",
                label: "v_shape_billboard",
                price: billboardPrice.toString(),
                includedInPack: pack.packType === "premium" && transactionType === "FOR_SALE",
                pathInStore: `optimizedEncoding.packs.billboard.${packType}`,
                isCheckbox: true
            });
        }

        /* ---------------- EXTENSION ---------------- */
        if (extensionOption) {
            const ext = perPackExtension(extensionOption, vis, packType);
            optionInformations.push({
                type: "extension",
                // Store the raw i18n key (not a pre-resolved string) so OptionCheckbox
                // translates it at render time and follows UI locale changes. IO-223
                label: "offer_duration_text_new",
                price: ext.price.toString(),
                pricePerWeek: ext.pricePerWeek.toString(),
                pathInStore: `optimizedEncoding.packs.extension.${packType}`,
                isCheckbox: true
            });
        }

        // Always sync pack price for selectedPack in store to be used on total price calculation
        if (vis) {
            store.commit("FormModule/addValueTransaction", {
                path: `optimizedEncoding.selectedPack.packPrice.${packType}`,
                value: vis.automaticDiscount != null ? vis.totalPrice : vis.price
            });
        }

        return {
            ...pack,
            packPrice: vis?.price ?? pack.packPrice,
            packPricePerWeek: vis?.pricePerWeek ?? pack.packPricePerWeek,
            packPriceTotal: vis?.totalPrice ?? pack.packPriceTotal,
            automaticDiscount: vis?.automaticDiscount ?? undefined,
            additionalOption: {
                label: "additional_options",
                optionInformations: optionInformations
            }
        };
    });
}

/**
 * Initialize packs for the welcome page
 */
export function configurePacksOnWelcomePageInit(
    storedPacks: any,
    transactionType: string
): OptimizedPack[] {
    return defaultPackConfig.map(pack => {
        const storedPack = storedPacks?.[pack.packType];

        const optionInformations: PackOptionInformation[] = [];
        const isFreeInPack = pack.packType === "premium" && transactionType === "FOR_SALE";
        const hasBillboardPrice = storedPack?.billboardPrice != null;

        if (isFreeInPack || hasBillboardPrice) {
            optionInformations.push({
                type: "billboard",
                label: "v_shape_billboard",
                price: isFreeInPack ? "0" : (storedPack?.billboardPrice?.toString() ?? "0"),
                includedInPack: isFreeInPack,
                pathInStore: `optimizedEncoding.packs.billboard.${pack.packType}`,
                isCheckbox: !isFreeInPack
            });
        }

        return {
            ...pack,
            packPrice: storedPack?.price ?? pack.packPrice,
            packPricePerWeek: "0",
            packPriceTotal: storedPack?.totalPrice ?? pack.packPriceTotal,
            automaticDiscount: storedPack?.automaticDiscount ?? undefined,
            additionalOption: {
                label: "additional_options",
                optionInformations: optionInformations
            }
        };
    });
}